<template>
    <v-app class="app globalFont">
        <v-app-bar
            v-if="topbar || safeAreaInsetTop > 0"
            class="app_title"
            :height="topbarHeight"
            outlined
        >
            <template v-if="topbar">
                <v-toolbar-title class="float-left">
                    <router-link :to="'/home/'">
                        <v-img
                            contain
                            position="left center"
                            max-height="56px"
                            src="/img/logo.png"
                        />
                    </router-link>{{ title }}
                </v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn v-if="$oidc.isAuthenticated" icon to="/profile">
                    <v-icon color="#ffffff">mdi-account-circle-outline</v-icon>
                </v-btn>
            </template>
        </v-app-bar>
        <v-main>
            <router-view></router-view>
            <EnrollDialog :key="enrollUi.key" :img-src="enrollUi.imageSrc" />
            <UnpairDialog />

            <AppNotificationDialog v-model="statusDialogOpen"
                                   :tone="currentTone"
                                   :title="currentTitle"
                                   :message="currentMessage"
                                   :options="{ width: 480, zIndex: 320 }"
                                   @OnOk="dismissStatusDialog"
                                   @OnCancel="dismissStatusDialog" />
            <v-dialog :value="sessionExpiryPrompt.isOpen"
                      persistent
                      max-width="440"
                      content-class="session-expiry-dialog">
                <v-card class="backgroundWhite session-expiry-card">
                    <v-card-title>
                        <v-icon color="warning" class="mr-2">mdi-clock-alert-outline</v-icon>
                        Session ending soon
                    </v-card-title>
                    <v-card-text>
                        <div v-if="sessionExpiryPrompt.renewalFailed">
                            We couldn't renew your session automatically. Choose Stay signed in to re-authenticate.
                        </div>
                        <div v-else>
                            Your sign-in is about to expire. Choose Stay signed in to continue using the app.
                        </div>
                        <div class="session-expiry-countdown" role="status" aria-live="polite">
                            You will be logged out in <strong>{{ sessionExpiryPrompt.secondsRemaining }}</strong> seconds.
                        </div>
                        <div v-if="sessionExpiryPrompt.errorMessage"
                             class="error--text mt-2"
                             role="alert">
                            {{ sessionExpiryPrompt.errorMessage }}
                        </div>
                    </v-card-text>
                    <v-card-actions>
                        <v-btn text color="grey darken-1" @click="logOutFromSessionPrompt">
                            Log out now
                        </v-btn>
                        <v-spacer></v-spacer>
                        <v-btn color="primary"
                               :loading="sessionExpiryPrompt.isSigningIn"
                               :disabled="sessionExpiryPrompt.isSigningIn"
                               @click="staySignedIn">
                            Stay signed in
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-main>

        <app-footer v-if="footer"></app-footer>
        <LayoutDiagnostics v-if="showLayoutDiagnostics" />

        <v-overlay :value="isProcessing" opacity="0.9" z-index="1000">
            <v-row align="center" justify="center" class="fill-height">
                <v-col cols="12" class="text-center white--text">
                    <v-progress-circular indeterminate size="50" color="white"></v-progress-circular>
                    <div class="mt-4 font-weight-bold">{{ processingLabel }}</div>
                    <div class="text-caption">Refreshing in {{ countdown }} seconds</div>
                </v-col>
            </v-row>
        </v-overlay>
    </v-app>
</template>

<script lang="ts">
    import AppFooter from "@/components/FooterNavigation.vue";
    import "./style/app.scss";
    import { app } from "@/store";
    import { enrollUi } from '@/store/enrollUi';
    import {
        logOutFromSessionPrompt,
        sessionExpiryPrompt,
        staySignedIn
    } from "@/store/services/security.service";
    import { computed, defineComponent, onBeforeUnmount, onMounted, ref, watch } from "vue";
    import useServiceUpdate from "@/utilities/useServiceUpdate";

    export default defineComponent({
        components: {
            AppFooter,
            LayoutDiagnostics: () => import("@/components/LayoutDiagnostics.vue"),
            EnrollDialog: () => import('@/views/components/Dialogs/EnrollDialog.vue'),
            UnpairDialog: () => import('@/views/components/Dialogs/UnpairDialog.vue'),
            AppNotificationDialog: () => import('@/views/components/Dialogs/AppNotificationDialog.vue')
        },
        setup() {
            useServiceUpdate(); // sets up auto-refresh behavior
            const title = computed<string>(() => app.title ? app.title : "");
            const topbar = computed<boolean>(() => app.showTopBar);
            const footer = computed<boolean>(() => app.showFooter);
            const readSafeAreaInsetTop = () => {
                const safeAreaInset = Number.parseFloat(
                    getComputedStyle(document.documentElement).getPropertyValue("--safe-area-inset-top")
                );
                return Number.isFinite(safeAreaInset) ? safeAreaInset : 0;
            };
            const safeAreaInsetTop = ref(readSafeAreaInsetTop());
            const isCompactToolbar = ref(window.matchMedia("(max-width: 960px)").matches);
            const topbarHeight = computed(() => {
                const contentHeight = topbar.value ? (isCompactToolbar.value ? 56 : 64) : 0;
                return contentHeight + safeAreaInsetTop.value;
            });
            const updateTopbarHeight = () => {
                safeAreaInsetTop.value = readSafeAreaInsetTop();
                isCompactToolbar.value = window.matchMedia("(max-width: 960px)").matches;
            };
            const showLayoutDiagnostics = computed<boolean>(() => app.showLayoutDiagnostics);


            onMounted(() => {
                updateTopbarHeight();
                window.addEventListener("resize", updateTopbarHeight);
            });

            const isProcessing = computed(() => app.isProcessing);
            const processingLabel = computed(() => app.processingLabel);
            const countdown = computed(() => app.countdown);

            const error = computed<string>(() => app.error ?? "");
            const alarm = computed<string>(() => app.alarm ?? "");
            const success = computed<string>(() => app.success ?? "");
            const alert = computed<string>(() => app.alert ?? "");

            // --- unified dialog state ---
            const statusDialogOpen = ref(false);
            const currentTone = ref<'warning' | 'alarm' | 'alert' | 'success'>('warning');
            const currentTitle = ref<string>('');
            const currentMessage = ref<string>('');
            let errorDismissTimer: ReturnType<typeof setTimeout> | null = null;
            let successDismissTimer: ReturnType<typeof setTimeout> | null = null;

            function clearDialogTimers() {
                if (errorDismissTimer) {
                    clearTimeout(errorDismissTimer);
                    errorDismissTimer = null;
                }

                if (successDismissTimer) {
                    clearTimeout(successDismissTimer);
                    successDismissTimer = null;
                }
            }

            // Priority: alarm > alert > warning(error) > success
            function refreshDialogFromStore() {
                if (alarm.value) {
                    currentTone.value = 'alarm';
                    currentTitle.value = 'Alarm triggered';
                    currentMessage.value = alarm.value;
                    statusDialogOpen.value = true;
                    return;
                }
                if (alert.value) {
                    currentTone.value = 'alert';
                    currentTitle.value = 'Alert';
                    currentMessage.value = alert.value;
                    statusDialogOpen.value = true;
                    return;
                }
                if (error.value) {
                    currentTone.value = 'warning';
                    currentTitle.value = 'Warning';
                    currentMessage.value = error.value;
                    statusDialogOpen.value = true;
                    return;
                }
                if (success.value) {
                    currentTone.value = 'success';
                    currentTitle.value = 'Success';
                    currentMessage.value = success.value;
                    statusDialogOpen.value = true;
                    return;
                }
                // none active
                statusDialogOpen.value = false;
                currentMessage.value = '';
            }

            // Dismiss clears only the active source in priority order
            function dismissStatusDialog() {
                switch (currentTone.value) {
                    case 'alarm': app.alarm = ""; break;
                    case 'alert': app.alert = ""; break;
                    case 'warning': app.error = ""; break;
                    case 'success': app.success = ""; break;
                }
                // refresh will close or move to next pending tone
                refreshDialogFromStore();
            }

            // Watch store fields and update the unified dialog
            watch(() => app.alarm, () => refreshDialogFromStore(), { immediate: true });
            watch(() => app.alert, () => refreshDialogFromStore(), { immediate: true });
            watch(() => app.error, (nv) => {
                refreshDialogFromStore();
                if (!nv) {
                    if (errorDismissTimer) {
                        clearTimeout(errorDismissTimer);
                        errorDismissTimer = null;
                    }
                    app.errorAutoDismiss = true;
                    app.errorAutoDismissExplicit = false;
                    return;
                }

                if (!app.errorAutoDismissExplicit) {
                    app.errorAutoDismiss = true;
                }

                if (app.errorAutoDismiss) {
                    if (errorDismissTimer) {
                        clearTimeout(errorDismissTimer);
                    }
                    const timeoutMs = app.errorAutoDismissMs ?? 10000;
                    errorDismissTimer = setTimeout(() => {
                        app.error = "";
                        errorDismissTimer = null;
                    }, timeoutMs);
                }
            }, { immediate: true });
            watch(() => app.success, (nv) => {
                refreshDialogFromStore();
                if (successDismissTimer) {
                    clearTimeout(successDismissTimer);
                    successDismissTimer = null;
                }
                if (nv) {
                    successDismissTimer = setTimeout(() => {
                        app.success = "";
                        successDismissTimer = null;
                    }, 10000); // auto-dismiss
                }
            }, { immediate: true });

            // Also close if the user clicks out or ESC via v-model
            watch(statusDialogOpen, (open) => {
                if (!open) {
                    // user closed the dialog -> clear whichever tone is active
                    dismissStatusDialog();
                }
            });

            onBeforeUnmount(() => {
                clearDialogTimers();
                window.removeEventListener("resize", updateTopbarHeight);
            });

            return {
                title,
                topbar,
                safeAreaInsetTop,
                topbarHeight,
                footer,
                showLayoutDiagnostics,
                isProcessing,
                processingLabel,
                countdown,
                error,
                alarm,
                success,
                alert,
                statusDialogOpen,
                currentTone,
                currentTitle,
                currentMessage,
                dismissStatusDialog,
                sessionExpiryPrompt,
                staySignedIn,
                logOutFromSessionPrompt,
                enrollUi
            };
        }
    });
</script>

<style scoped>
    .alert-on-top {
        position: fixed;
        top: 10%;
        left: 5%;
        right: 5%;
        z-index: 9999;
    }

    .custom-alert-close {
        width: 60px;
        height: 60px;
        font-size: 24px;
    }

    .custom-alert-close-icon {
        font-size: 40px;
    }

    @import url('https://fonts.googleapis.com/css2?family=Open+Sans&display=swap');
    /*@import url('https://fonts.googleapis.com/css2?family=Dancing+Script&display=swap');*/
    /*@font-face {
        font-family: 'Dancing Script';
        src: local('Dancing Script'), url('~@assets/fonts/DancingScript.ttf') format('truetype')
    }*/
</style>